import { motion, MotionConfig, useInView, useReducedMotion } from 'framer-motion';
import type { Variants } from 'framer-motion';
import {
  BadgeCheck, Briefcase, Check, ClipboardCheck, GraduationCap,
  MessageCircle, Mic, Phone, Play, Quote, Radio, Star, Trophy,
} from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link } from 'react-router-dom';
import { WhatsAppIcon } from '../components/SocialIcons';
import logo from '../logo/lo.png';

// ─── CONFIG ─────────────────────────────────────────────────────────────────────
const PHONE = '9894449002';
const PHONE_LABEL = '98944 49002';
const WHATSAPP_URL = `https://wa.me/91${PHONE}?text=${encodeURIComponent("Hi Vista Tech, I saw the mentors page. I'd like to learn with Jamuna and Rabbik. Can you tell me about the course?")}`;
const GOOGLE_REVIEWS_URL = 'https://maps.app.goo.gl/sjz7bY3cbBdHQ1t3A';
const PAGE_URL = 'https://vistatech.org/mentors';

// ─── COLORS (brand tokens from tailwind.config.js, plus a deeper night for the dark bands) ─
const COLOR = {
  night: '#130d1d',
  nightSoft: '#1d1430',
  plum: '#2b2240',
  violet: '#4e3a69',
  gold: '#F0B429',
  goldDeep: '#C8860F',
  goldWash: '#fff5db',
  orange: '#FF6535',
  paper: '#ffffff',
  mist: '#f7f4fb',
  ink: '#1c1529',
  inkSoft: '#5c5270',
  inkFaint: '#8d84a1',
  rule: 'rgba(43, 34, 64, 0.12)',
  onDarkSoft: 'rgba(255, 255, 255, 0.74)',
};

const FONT_BODY = "'Plus Jakarta Sans', Inter, system-ui, -apple-system, sans-serif";
const FONT_SERIF = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const NIGHT = `linear-gradient(165deg, ${COLOR.night} 0%, ${COLOR.nightSoft} 55%, ${COLOR.plum} 100%)`;

// ─── PHOTOS (Cloudinary — c_thumb + g_face gives both mentors the same head-and-shoulders crop) ─
const CLOUDINARY = 'https://res.cloudinary.com/zmdfv4o7';
const portraitUrl = (id: string, w: number) => `${CLOUDINARY}/image/upload/f_auto,q_auto,c_thumb,g_face,ar_4:5,z_0.5,w_${w}/${id}`;
const avatarUrl = (id: string, w: number) => `${CLOUDINARY}/image/upload/f_auto,q_auto,c_thumb,g_face,ar_1:1,z_0.7,w_${w}/${id}`;
// a Cloudinary video is also its own poster: same path with a .jpg extension
const posterFor = (videoSrc: string) => (videoSrc.includes('res.cloudinary.com')
  ? videoSrc.replace('/video/upload/', '/video/upload/f_auto,q_auto,w_400/').replace(/\.\w+$/, '.jpg')
  : undefined);

// ─── CONTENT ────────────────────────────────────────────────────────────────────
type Mentor = { id: string; name: string; role: string; focus: string; trained: string; photo: string; tagline: string; bio: string; skills: string[] };
type StoryVideo = { id: string; name?: string; role?: string; company?: string; pay?: string; background?: string; videoSrc: string; poster?: string };

// Both are senior mentors; the first one leads (front card in the hero, first profile).
// What each mentor teaches is taken from their class logs (src/tracker/excel/class-log-import.csv)
const MENTORS: Mentor[] = [
  {
    id: 'jamuna',
    name: 'Jamuna',
    role: 'Senior Mentor',
    focus: 'AI, Machine Learning & Full Stack',
    trained: '100+',
    photo: 'v1792575412/IMG-20260205-WA0030.jpg.jpg',
    tagline: 'From Python basics to machine learning models.',
    bio: 'Jamuna is a Senior Mentor at Vista Tech, and the classes go deep: Python and object-oriented programming, SQL practice on LeetCode problems, and the full machine learning path, from regression and decision trees to random forests, clustering, NLP and neural networks. Full stack students also build with Django and React.',
    skills: ['Python & OOP', 'SQL', 'NumPy & pandas', 'Machine learning', 'NLP', 'Neural networks', 'Django', 'React'],
  },
  {
    id: 'rabbik',
    name: 'Rabbik',
    role: 'Senior Mentor',
    focus: 'Data Analytics',
    trained: '100+',
    photo: 'v1792575408/file_0000000083448208b5f282bd2020f756.png',
    tagline: 'From your first formula to your first dashboard.',
    bio: 'Rabbik is a Senior Mentor at Vista Tech. Classes start from the basics, Excel and Google Sheets, and move step by step into SQL joins, subqueries and window functions, Python with pandas, statistics and Power BI. Every class ends with a task, and every task gets checked.',
    skills: ['Excel & Google Sheets', 'SQL', 'Python', 'pandas', 'Statistics', 'Power BI', 'Data visualisation'],
  },
];

const HERO = {
  eyebrow: 'Meet your senior mentors',
  headline: "Learn from mentors who've each trained",
  headlineAccent: '100+ students.',
  sub: 'Jamuna and Rabbik teach you live, every day — from your first Excel formula to SQL, Python and machine learning — and get you ready for the interview that starts your career.',
  primaryCta: 'Talk to us on WhatsApp',
  secondaryCta: 'Watch placement stories',
  proof: 'Vista Tech students now work at Citi, Cognizant, Kyndryl and more.',
  proofFaces: [
    `${CLOUDINARY}/image/upload/f_auto,q_auto,c_thumb,g_face,ar_1:1,w_72/v1790241677/Meghana-_Data_scientist.jpg`,
    `${CLOUDINARY}/image/upload/f_auto,q_auto,c_thumb,g_face,ar_1:1,w_72/v1790241678/Rohit_Prakash-Data_anlyst.png`,
    `${CLOUDINARY}/image/upload/f_auto,q_auto,c_thumb,g_face,ar_1:1,w_72/v1790241678/Ajay_Krishna_-_powerbi.png`,
  ],
};

const STATS = [
  { value: 100, suffix: '+', label: 'Students trained by each mentor' },
  { value: 50, suffix: '+', label: 'Students helped to crack internships' },
  { value: 80, suffix: '%+', label: 'Success rate' },
];

const MENTORS_INTRO = {
  eyebrow: 'Your mentors',
  heading: 'Two senior mentors. One goal:',
  headingAccent: 'your first job.',
  sub: 'Every class is live, with a mentor who checks your work and pushes you until you are ready for the interview.',
};

const METHOD = {
  eyebrow: 'How they mentor you',
  heading: 'Not just classes.',
  headingAccent: 'A mentor in your corner.',
  sub: 'What learning with Jamuna and Rabbik looks like, from the first class to the offer letter.',
  steps: [
    { Icon: Radio, title: 'Teaches you live, every day', body: 'Live classes, not pre-recorded videos. You can stop the class and ask. Taught in Tamil, with technical terms in English.' },
    { Icon: ClipboardCheck, title: 'Gives you a task after every class', body: 'Practice on real data, checked before the next class, so what you learn never stays as theory.' },
    { Icon: MessageCircle, title: 'Clears your doubts on WhatsApp', body: 'Stuck on a query at night? You do not have to wait for the next class to ask.' },
    { Icon: Mic, title: 'Runs mock interviews with you', body: 'You practise the real questions until the answers stop being a guess.' },
    { Icon: Briefcase, title: 'Helps you crack the internship and the job', body: '50+ candidates have cracked internships this way. Then the placement drive puts your profile in front of real openings.' },
  ],
};

const STORIES = {
  eyebrow: 'Placement stories',
  heading: 'They started where you are.',
  headingAccent: "Now they're placed.",
  sub: 'Vista Tech students on how they got the job, in their own words.',
  cta: 'Want a story like this? Talk to us',
};

// the same placement videos as /analyst (VIDEO_TESTIMONIALS in Analyst.tsx)
const PLACEMENT_VIDEOS: StoryVideo[] = [
  { id: 'yamini', name: 'Yamini', role: 'Data Analyst', company: 'Citi', pay: '4.9 LPA', background: '2025 passed out', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1790240837/yamini-1.mp4', poster: 'https://res.cloudinary.com/zmdfv4o7/video/upload/f_auto,q_auto,w_400/v1790240837/yamini-1.jpg' },
  { id: 'jamuna-citi', name: 'Jamuna', role: 'Technical Analyst', company: 'Citi', pay: '4.9 LPA', background: 'Career gap', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1790240933/yellow_shirt_girl-1.mp4', poster: 'https://res.cloudinary.com/zmdfv4o7/video/upload/f_auto,q_auto,w_400/v1790240933/yellow_shirt_girl-1.jpg' },
  { id: 'vasikaramani', name: 'Vasikaramani.M', role: 'Data Processing Analyst', company: 'Roop Namyang', pay: '4.2 LPA', background: 'Career gap', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1790241023/WhatsApp_Video_2026-09-24_at_2.38.03_PM.mp4', poster: 'https://res.cloudinary.com/zmdfv4o7/video/upload/f_auto,q_auto,w_400/v1790241023/WhatsApp_Video_2026-09-24_at_2.38.03_PM.jpg' },
  { id: 'monish', name: 'Monish', role: 'Business Analyst', company: 'Ifluids Engineering', pay: '3 LPA', background: 'Fresher', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1790240956/VID_20260923_133449.mp4', poster: 'https://res.cloudinary.com/zmdfv4o7/video/upload/f_auto,q_auto,w_400/v1790240956/VID_20260923_133449.jpg' },
];

const FEEDBACK = {
  eyebrow: 'Student reviews',
  heading: 'Hear it from',
  headingAccent: 'their students.',
  sub: 'Students who learned with Jamuna and Rabbik, in their own words.',
};

// Students' video reviews of the mentors. The section only shows while this has entries.
// A Cloudinary video link is enough: the poster comes from the same upload, and a card
// without a name shows just the video.
const FEEDBACK_VIDEOS: StoryVideo[] = [
  { id: 'review-1', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1792582715/WhatsApp_Video_2026-10-21_at_3.08.05_PM_1.mp4' },
  { id: 'review-2', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1792582715/WhatsApp_Video_2026-10-21_at_3.06.57_PM.mp4' },
  { id: 'review-3', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1792582617/WhatsApp_Video_2026-10-21_at_3.08.05_PM.mp4' },
  { id: 'review-4', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1792582612/WhatsApp_Video_2026-10-21_at_3.06.43_PM.mp4' },
  { id: 'review-5', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1792587153/WhatsApp_Video_2026-10-21_at_3.08.05_PM_3.mp4' },
];

const REVIEWS = {
  eyebrow: 'Google reviews',
  heading: 'What students say',
  headingAccent: 'on Google.',
  rating: '4.8',
  linkLabel: 'Read all reviews on Google',
  // copied from the Google reviews already on /analyst and the Success Stories page; the first one is featured
  items: [
    { name: 'Rajeshwari S', rating: 5, text: "Vista Tech transformed my skills! I joined without any coding background, and now I'm developing full-stack apps confidently. Projects are real-world oriented — not just theory. Totally worth it!" },
    { name: 'Logesh S', rating: 5, text: 'Excellent institute for both learning and placement. Trainers ensure you understand concepts deeply, and the placement support is outstanding.' },
    { name: 'Ganesh Kota', rating: 4, text: 'The trainers are very experienced and explain even complex topics simply. Practical training, real-time projects and excellent placement assistance throughout.' },
    { name: 'Dr. Arul Mathi', rating: 5, text: "We reached out to Vista Tech for our sister's training. With your guidance she got placed as a Junior Data Analyst within 2 months. Thank you for all your help!" },
    { name: 'Pooja Sivakumar', rating: 5, text: 'I completed my Java Full Stack Development course here, and it was an amazing experience. Thanks to their guidance and placement support, I got placed in a good company. Totally recommend this institute!' },
  ],
};

const CLOSING = {
  eyebrow: 'Your next step',
  heading: 'Your first job starts with',
  headingAccent: 'the right mentor.',
  body: 'Message us on WhatsApp. Tell us what you studied and the job you want, and we will tell you honestly whether the course is right for you.',
  trust: ['Free counselling call', 'Pay 50% of the fee only after you are placed', 'Classes taught in Tamil'],
};

const FOOTER_INFO = {
  line: 'Vista Tech Solutions · IT training and HR consulting',
  address: 'No.115, 7th Floor, Metro Towers, PH Road, Egmore, Chennai 600084',
  links: [
    { label: 'Home', to: '/' },
    { label: 'Courses', to: '/courses' },
    { label: 'Success stories', to: '/success-stories' },
    { label: 'Terms', to: '/Terms-and-Conditions' },
  ],
};

const AVATAR_PALETTE = ['#4e3a69', '#C8860F', '#FF6535', '#1e7a4f', '#2563eb'];

// ─── MOTION ─────────────────────────────────────────────────────────────────────
const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];
const fadeUp: Variants = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE } } };
const stagger: Variants = { hidden: {}, show: { transition: { staggerChildren: 0.09 } } };

// ─── SEO ────────────────────────────────────────────────────────────────────────
function useSEO() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const prevTitle = document.title;
    document.title = 'Meet Your Mentors · Jamuna & Rabbik | Vista Tech';

    // point the shared tags at this page, and put them back on the way out
    const swaps: Array<[Element, string, string | null]> = [];
    const swap = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (!el) return;
      swaps.push([el, attr, el.getAttribute(attr)]);
      el.setAttribute(attr, value);
    };
    swap('meta[name="description"]', 'content', 'Learn live from Vista Tech senior mentors Jamuna and Rabbik. Each has trained 100+ students. 50+ students helped to crack internships, 80%+ success rate.');
    swap('meta[property="og:title"]', 'content', 'Meet your senior mentors at Vista Tech: Jamuna and Rabbik');
    swap('meta[property="og:description"]', 'content', 'Two senior mentors who have each trained 100+ students. 50+ helped to crack internships. 80%+ success rate. Watch their students talk about it.');
    swap('meta[property="og:url"]', 'content', PAGE_URL);
    swap('link[rel="canonical"]', 'href', PAGE_URL);

    if (!document.getElementById('mentors-fonts')) {
      const link = document.createElement('link');
      link.id = 'mentors-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap';
      document.head.appendChild(link);
    }

    return () => {
      document.title = prevTitle;
      swaps.forEach(([el, attr, value]) => { if (value === null) el.removeAttribute(attr); else el.setAttribute(attr, value); });
    };
  }, []);
}

// ─── SMALL PIECES ───────────────────────────────────────────────────────────────
const container: React.CSSProperties = { maxWidth: 1180, margin: '0 auto', padding: '0 20px' };

const scrollToId = (id: string) => (e: React.MouseEvent) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// lo.png is a 500×500 canvas; the mark itself sits in the box (138,178)–(359,338)
function LogoMark({ height }: { height: number }) {
  const s = height / 160;
  return (
    <span style={{ position: 'relative', display: 'block', width: Math.round(221 * s), height, overflow: 'hidden' }}>
      <img src={logo} alt="Vista Tech" style={{ position: 'absolute', left: -138 * s, top: -178 * s, width: 500 * s, height: 500 * s, maxWidth: 'none' }} />
    </span>
  );
}

function WhatsAppButton({ label, small = false }: { label: string; small?: boolean }) {
  return (
    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={`m-btn m-btn--gold${small ? ' m-btn--sm' : ''}`}>
      <WhatsAppIcon size={small ? 16 : 19} /> {label}
    </a>
  );
}

function Glow({ color, style }: { color: string; style: React.CSSProperties }) {
  return <span aria-hidden="true" style={{ position: 'absolute', borderRadius: '50%', background: `radial-gradient(closest-side, ${color}, transparent)`, pointerEvents: 'none', ...style }} />;
}

function Eyebrow({ children, onDark = false }: { children: React.ReactNode; onDark?: boolean }) {
  return (
    <p style={{ display: 'inline-flex', alignItems: 'center', gap: 10, margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: onDark ? COLOR.gold : COLOR.violet }}>
      <span aria-hidden="true" style={{ width: 22, height: 2, borderRadius: 2, background: onDark ? COLOR.gold : COLOR.orange }} />
      {children}
    </p>
  );
}

// one phrase per heading is set in the serif italic; `block` puts a whole second sentence on its own line
function Accent({ children, onDark = false, block = false }: { children: React.ReactNode; onDark?: boolean; block?: boolean }) {
  return <span style={{ display: block ? 'block' : undefined, fontFamily: FONT_SERIF, fontStyle: 'italic', fontWeight: 400, fontSize: '1.12em', letterSpacing: '-0.01em', color: onDark ? COLOR.gold : COLOR.violet }}>{children}</span>;
}

function SectionHeading({ eyebrow, heading, accent, sub, onDark = false, accentBlock = false, className }: { eyebrow: string; heading: string; accent: string; sub?: string; onDark?: boolean; accentBlock?: boolean; className?: string }) {
  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Eyebrow onDark={onDark}>{eyebrow}</Eyebrow>
      <h2 style={{ margin: '16px 0 0', maxWidth: '22ch', fontSize: 'clamp(30px, 4.4vw, 50px)', fontWeight: 700, letterSpacing: '-0.035em', lineHeight: 1.08, color: onDark ? '#fff' : COLOR.ink }}>
        {heading} <Accent onDark={onDark} block={accentBlock}>{accent}</Accent>
      </h2>
      {sub && <p style={{ margin: '16px 0 0', maxWidth: '52ch', fontSize: 'clamp(15.5px, 1.6vw, 17.5px)', lineHeight: 1.65, color: onDark ? COLOR.onDarkSoft : COLOR.inkSoft }}>{sub}</p>}
    </div>
  );
}

function Stars({ rating, size = 15 }: { rating: number; size?: number }) {
  return (
    <span role="img" aria-label={`${rating} out of 5 stars`} style={{ display: 'inline-flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(i => <Star key={i} size={size} aria-hidden="true" style={{ color: '#FBBC05', fill: i <= rating ? '#FBBC05' : 'none' }} />)}
    </span>
  );
}

const GoogleG = ({ size = 28 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.93 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

function CountUp({ to }: { to: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const reduce = useReducedMotion();
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    if (reduce) { setN(to); return; }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / 1400);
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, reduce, to]);
  return <span ref={ref}>{n}</span>;
}

// ─── HEADER ─────────────────────────────────────────────────────────────────────
function MentorsHeader() {
  return (
    <header className="m-header">
      <div style={{ ...container, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <Link to="/" aria-label="Vista Tech home" className="m-logo-pill"><LogoMark height={38} /></Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <a href={`tel:${PHONE}`} className="m-header-phone"><Phone size={15} aria-hidden="true" /> {PHONE_LABEL}</a>
          <WhatsAppButton label="WhatsApp us" small />
        </div>
      </div>
    </header>
  );
}

// ─── HERO ───────────────────────────────────────────────────────────────────────
// both mentors are senior, so both cards get the gold ring; the lead card is only bigger and in front
function PortraitCard({ m }: { m: Mentor }) {
  return (
    <figure style={{
      position: 'relative', margin: 0, aspectRatio: '4 / 5', borderRadius: 26, overflow: 'hidden', background: COLOR.nightSoft,
      boxShadow: `0 0 0 2px ${COLOR.gold}, 0 44px 80px -34px rgba(0,0,0,0.75)`,
    }}>
      <img src={portraitUrl(m.photo, 640)} srcSet={`${portraitUrl(m.photo, 360)} 360w, ${portraitUrl(m.photo, 640)} 640w`} sizes="(min-width: 960px) 300px, 46vw"
        alt={`${m.name}, ${m.role} at Vista Tech`} width={640} height={800} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
      <figcaption className="m-portrait-cap" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '56px 16px 15px', background: 'linear-gradient(180deg, rgba(12,8,20,0) 0%, rgba(12,8,20,0.88) 100%)' }}>
        <span style={{ display: 'block', fontFamily: FONT_SERIF, fontStyle: 'italic', fontSize: 'clamp(25px, 3.2vw, 34px)', lineHeight: 1, color: '#fff' }}>{m.name}</span>
        <span style={{ display: 'block', marginTop: 7, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: COLOR.gold }}>{m.role}</span>
      </figcaption>
    </figure>
  );
}

function FloatChip({ className, icon, strong, text, delay }: { className: string; icon: React.ReactNode; strong: string; text: string; delay: number }) {
  return (
    <div className={`m-chip ${className}`} aria-hidden="true">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay, ease: EASE }}>
        <div className="m-bob m-bob--slow" style={{ display: 'inline-flex', alignItems: 'center', gap: 11, padding: '10px 18px 10px 10px', borderRadius: 16, background: 'rgba(255,255,255,0.97)', color: COLOR.ink, boxShadow: '0 18px 40px -16px rgba(0,0,0,0.55)', whiteSpace: 'nowrap' }}>
          <span style={{ width: 38, height: 38, borderRadius: 11, display: 'grid', placeItems: 'center', background: COLOR.goldWash, color: COLOR.goldDeep }}>{icon}</span>
          <span style={{ fontSize: 13, lineHeight: 1.25, color: COLOR.inkSoft }}>
            <strong style={{ display: 'block', fontSize: 19, fontWeight: 800, letterSpacing: '-0.03em', color: COLOR.ink }}>{strong}</strong>
            {text}
          </span>
        </div>
      </motion.div>
    </div>
  );
}

function HeroPortraits() {
  const [lead, second] = MENTORS;
  return (
    <div className="m-portraits">
      <div className="m-portrait m-portrait--lead">
        <motion.div initial={{ opacity: 0, y: 34 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, delay: 0.1, ease: EASE }}>
          <div className="m-bob"><PortraitCard m={lead} /></div>
        </motion.div>
      </div>
      <div className="m-portrait m-portrait--second">
        <motion.div initial={{ opacity: 0, y: 34 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, delay: 0.25, ease: EASE }}>
          <div className="m-bob m-bob--late"><PortraitCard m={second} /></div>
        </motion.div>
      </div>
      <FloatChip className="m-chip--a" icon={<GraduationCap size={19} />} strong="100+ each" text="students trained" delay={0.7} />
      <FloatChip className="m-chip--b" icon={<Trophy size={19} />} strong="80%+" text="success rate" delay={0.9} />
    </div>
  );
}

function Hero() {
  return (
    <section className="m-hero" style={{ position: 'relative', overflow: 'hidden', background: NIGHT, color: '#fff' }}>
      <Glow color="rgba(240,180,41,0.24)" style={{ width: 780, height: 780, right: '-16%', top: '-16%' }} />
      <Glow color="rgba(255,101,53,0.16)" style={{ width: 640, height: 640, left: '-24%', bottom: '-36%' }} />
      <div aria-hidden="true" className="m-dots" />
      <div style={{ ...container, position: 'relative' }}>
        <div className="m-hero-grid">
          <div className="m-hero-photos"><HeroPortraits /></div>
          <motion.div className="m-hero-copy" initial="hidden" animate="show" variants={stagger}>
            <motion.div variants={fadeUp}><Eyebrow onDark>{HERO.eyebrow}</Eyebrow></motion.div>
            <motion.h1 variants={fadeUp} style={{ margin: '18px 0 0', maxWidth: '15ch', fontSize: 'clamp(34px, 5.4vw, 64px)', fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1.04 }}>
              {HERO.headline} <Accent onDark>{HERO.headlineAccent}</Accent>
            </motion.h1>
            <motion.p variants={fadeUp} style={{ margin: '20px 0 0', maxWidth: '46ch', fontSize: 'clamp(15.5px, 1.7vw, 18px)', lineHeight: 1.65, color: COLOR.onDarkSoft }}>{HERO.sub}</motion.p>
            <motion.div variants={fadeUp} className="m-hero-ctas">
              <WhatsAppButton label={HERO.primaryCta} />
              <a href="#stories" onClick={scrollToId('stories')} className="m-btn m-btn--ghost"><Play size={15} fill="currentColor" aria-hidden="true" /> {HERO.secondaryCta}</a>
            </motion.div>
            <motion.div variants={fadeUp} className="m-proof">
              <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                {HERO.proofFaces.map((src, i) => (
                  <img key={src} src={src} alt="" width={34} height={34} style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', marginLeft: i ? -10 : 0, boxShadow: `0 0 0 2px ${COLOR.nightSoft}` }} />
                ))}
              </span>
              <span style={{ fontSize: 13.5, lineHeight: 1.45, color: COLOR.onDarkSoft, textAlign: 'left', maxWidth: '30ch' }}>{HERO.proof}</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── TRACK RECORD ───────────────────────────────────────────────────────────────
function StatsBand() {
  return (
    <section aria-label="Track record" className="m-stats-section" style={{ position: 'relative', zIndex: 4 }}>
      <div style={container}>
        <motion.div className="m-stats" initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.65, ease: EASE }}>
          {STATS.map(s => (
            <div key={s.label} className="m-stat">
              <p className="m-stat-num">
                <span aria-hidden="true"><CountUp to={s.value} /><span style={{ color: COLOR.orange }}>{s.suffix}</span></span>
                <span className="m-sr">{s.value}{s.suffix}</span>
              </p>
              <p className="m-stat-label">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─── MENTOR PROFILES ────────────────────────────────────────────────────────────
function MentorProfile({ m, flip }: { m: Mentor; flip: boolean }) {
  return (
    <motion.article className={`m-profile${flip ? ' m-profile--flip' : ''}`} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }} variants={stagger}>
      <motion.div variants={fadeUp} className="m-profile-photo">
        <span aria-hidden="true" className="m-profile-frame" />
        <img src={portraitUrl(m.photo, 840)} srcSet={`${portraitUrl(m.photo, 480)} 480w, ${portraitUrl(m.photo, 840)} 840w`} sizes="(min-width: 900px) 440px, 92vw"
          alt={`${m.name}, ${m.role} at Vista Tech`} loading="lazy" width={840} height={1050}
          style={{ position: 'relative', display: 'block', width: '100%', height: 'auto', aspectRatio: '4 / 5', objectFit: 'cover', borderRadius: 28, boxShadow: '0 40px 70px -40px rgba(29,20,48,0.55)' }} />
        <span className="m-profile-badge"><BadgeCheck size={16} aria-hidden="true" /> {m.role}</span>
      </motion.div>
      <div>
        <motion.p variants={fadeUp} style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: COLOR.violet }}>
          <span aria-hidden="true" style={{ width: 22, height: 2, borderRadius: 2, background: COLOR.orange }} />
          {m.role} · {m.focus}
        </motion.p>
        <motion.h3 variants={fadeUp} style={{ margin: '12px 0 0', fontFamily: FONT_SERIF, fontStyle: 'italic', fontWeight: 400, fontSize: 'clamp(58px, 8vw, 96px)', lineHeight: 0.95, letterSpacing: '-0.02em', color: COLOR.plum }}>{m.name}</motion.h3>
        <motion.p variants={fadeUp} className="m-trained"><GraduationCap size={17} aria-hidden="true" /><span><strong>{m.trained}</strong> students trained</span></motion.p>
        <motion.p variants={fadeUp} style={{ margin: '16px 0 0', fontSize: 'clamp(18px, 2vw, 22px)', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.3, color: COLOR.ink }}>{m.tagline}</motion.p>
        <motion.p variants={fadeUp} style={{ margin: '14px 0 0', maxWidth: '56ch', fontSize: 16, lineHeight: 1.75, color: COLOR.inkSoft }}>{m.bio}</motion.p>
        <motion.div variants={fadeUp}>
          <p style={{ margin: '26px 0 0', fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: COLOR.inkFaint }}>What you learn with {m.name}</p>
          <ul className="m-skills">{m.skills.map(s => <li key={s}>{s}</li>)}</ul>
        </motion.div>
      </div>
    </motion.article>
  );
}

function MentorsSection() {
  return (
    <section id="mentors" className="m-pad" style={{ background: COLOR.paper }}>
      <div style={container}>
        <SectionHeading eyebrow={MENTORS_INTRO.eyebrow} heading={MENTORS_INTRO.heading} accent={MENTORS_INTRO.headingAccent} sub={MENTORS_INTRO.sub} />
        <div style={{ display: 'grid', gap: 'clamp(64px, 9vw, 112px)', marginTop: 'clamp(44px, 6vw, 76px)' }}>
          {MENTORS.map((m, i) => <MentorProfile key={m.id} m={m} flip={i % 2 === 1} />)}
        </div>
      </div>
    </section>
  );
}

// ─── HOW THEY MENTOR ────────────────────────────────────────────────────────────
function MethodSection() {
  return (
    <section className="m-pad" style={{ position: 'relative', overflow: 'hidden', background: NIGHT, color: '#fff' }}>
      <Glow color="rgba(240,180,41,0.15)" style={{ width: 720, height: 720, right: '-22%', top: '-34%' }} />
      <div style={{ ...container, position: 'relative' }}>
        <SectionHeading onDark accentBlock eyebrow={METHOD.eyebrow} heading={METHOD.heading} accent={METHOD.headingAccent} sub={METHOD.sub} />
        <motion.ol className="m-steps" initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }} variants={stagger}>
          {METHOD.steps.map(({ Icon, title, body }, i) => (
            <motion.li key={title} variants={fadeUp} className="m-step">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: FONT_SERIF, fontStyle: 'italic', fontSize: 42, lineHeight: 1, color: COLOR.gold }}>{String(i + 1).padStart(2, '0')}</span>
                <span style={{ width: 42, height: 42, borderRadius: 12, display: 'grid', placeItems: 'center', background: 'rgba(240,180,41,0.12)', color: COLOR.gold }}><Icon size={20} aria-hidden="true" /></span>
              </div>
              <h3 style={{ margin: '20px 0 0', fontSize: 17.5, fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.3 }}>{title}</h3>
              <p style={{ margin: '8px 0 0', fontSize: 14.5, lineHeight: 1.65, color: COLOR.onDarkSoft }}>{body}</p>
            </motion.li>
          ))}
        </motion.ol>
      </div>
    </section>
  );
}

// ─── VIDEOS ─────────────────────────────────────────────────────────────────────
type PlayingProps = { playing: string | null; setPlaying: (id: string | null) => void };

function VideoCard({ v, label, active, onPlay, onStop }: { v: StoryVideo; label: string; active: boolean; onPlay: () => void; onStop: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const poster = v.poster ?? posterFor(v.videoSrc);
  // mount the player and start it inside the tap itself: phones only allow sound from a direct tap
  const start = () => {
    flushSync(onPlay);
    videoRef.current?.play().catch(() => {});
  };
  return (
    <article className="m-video-card">
      <div className="m-video-frame">
        {active ? (
          <video ref={videoRef} src={v.videoSrc} poster={poster} controls playsInline onEnded={onStop}
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
        ) : (
          <button type="button" onClick={start} className="m-video-poster" aria-label={label}>
            {poster && <img src={poster} alt="" loading="lazy" style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />}
            <span aria-hidden="true" className="m-video-shade" />
            <span aria-hidden="true" className="m-video-play"><Play size={22} fill="currentColor" style={{ marginLeft: 3 }} /></span>
            {v.pay && <span aria-hidden="true" className="m-video-pay">{v.pay}</span>}
          </button>
        )}
      </div>
      {v.name && (
        <div style={{ padding: '14px 2px 0' }}>
          <p style={{ margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: '-0.015em', color: COLOR.ink }}>{v.name}</p>
          {v.role && (
            <p style={{ margin: '3px 0 0', fontSize: 14, lineHeight: 1.45, color: COLOR.inkSoft }}>
              {v.role}{v.company && <> · <strong style={{ fontWeight: 700, color: COLOR.ink }}>{v.company}</strong></>}
            </p>
          )}
          {v.background && <span className="m-video-bg">{v.background}</span>}
        </div>
      )}
    </article>
  );
}

function VideoRow({ items, prefix, playing, setPlaying }: PlayingProps & { items: StoryVideo[]; prefix: string }) {
  // on desktop the row is a grid with one column per video, up to five
  return (
    <div className="m-video-row" style={{ '--cols': Math.min(items.length, 5) } as React.CSSProperties}>
      {items.map((v, i) => {
        const id = `${prefix}:${v.id}`;
        const label = v.name ? `Play ${v.name}'s video${v.pay ? `, placed at ${v.pay}` : ''}` : `Play student video ${i + 1} of ${items.length}`;
        return <VideoCard key={id} v={v} label={label} active={playing === id} onPlay={() => setPlaying(id)} onStop={() => setPlaying(null)} />;
      })}
    </div>
  );
}

function StoriesSection({ playing, setPlaying }: PlayingProps) {
  return (
    <section id="stories" className="m-pad" style={{ background: COLOR.mist }}>
      <div style={container}>
        <SectionHeading accentBlock eyebrow={STORIES.eyebrow} heading={STORIES.heading} accent={STORIES.headingAccent} sub={STORIES.sub} />
        <div style={{ marginTop: 22 }}>
          <VideoRow items={PLACEMENT_VIDEOS} prefix="placement" playing={playing} setPlaying={setPlaying} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
          <WhatsAppButton label={STORIES.cta} />
        </div>
      </div>
    </section>
  );
}

function FeedbackSection({ playing, setPlaying }: PlayingProps) {
  if (FEEDBACK_VIDEOS.length === 0) return null;
  return (
    <section id="student-reviews" className="m-pad" style={{ background: COLOR.paper }}>
      <div style={container}>
        <SectionHeading eyebrow={FEEDBACK.eyebrow} heading={FEEDBACK.heading} accent={FEEDBACK.headingAccent} sub={FEEDBACK.sub} />
        <div style={{ marginTop: 22 }}>
          <VideoRow items={FEEDBACK_VIDEOS} prefix="feedback" playing={playing} setPlaying={setPlaying} />
        </div>
      </div>
    </section>
  );
}

// ─── GOOGLE REVIEWS ─────────────────────────────────────────────────────────────
function ReviewsSection() {
  const [featured, ...rest] = REVIEWS.items;
  return (
    <section className="m-pad" style={{ background: FEEDBACK_VIDEOS.length > 0 ? COLOR.mist : COLOR.paper }}>
      <div style={container}>
        <div className="m-reviews-head">
          <SectionHeading className="m-head-left" eyebrow={REVIEWS.eyebrow} heading={REVIEWS.heading} accent={REVIEWS.headingAccent} />
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="m-rating">
            <GoogleG size={34} />
            <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <strong style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: COLOR.ink }}>{REVIEWS.rating}</strong>
                <Stars rating={5} size={16} />
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: COLOR.violet }}>{REVIEWS.linkLabel} →</span>
            </span>
          </a>
        </div>

        <div className="m-reviews">
          <motion.figure className="m-review-featured" initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.65, ease: EASE }}>
            <Quote size={36} aria-hidden="true" style={{ color: COLOR.gold }} />
            <blockquote style={{ margin: '18px 0 0', fontSize: 'clamp(19px, 2.1vw, 24px)', fontWeight: 500, lineHeight: 1.5, letterSpacing: '-0.01em' }}>{featured.text}</blockquote>
            <figcaption style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 'auto', paddingTop: 26 }}>
              <span style={{ width: 44, height: 44, borderRadius: '50%', display: 'grid', placeItems: 'center', background: COLOR.gold, color: COLOR.ink, fontWeight: 800, fontSize: 17 }}>{featured.name.charAt(0)}</span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <strong style={{ fontSize: 15.5, fontWeight: 700 }}>{featured.name}</strong>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: COLOR.onDarkSoft }}><Stars rating={featured.rating} size={13} /> Google review</span>
              </span>
            </figcaption>
          </motion.figure>

          <motion.div className="m-review-grid" initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }} variants={stagger}>
            {rest.map((r, i) => (
              <motion.figure key={r.name} variants={fadeUp} className="m-review">
                <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <span style={{ flexShrink: 0, width: 38, height: 38, borderRadius: '50%', display: 'grid', placeItems: 'center', background: AVATAR_PALETTE[i % AVATAR_PALETTE.length], color: '#fff', fontWeight: 800, fontSize: 15 }}>{r.name.replace('Dr. ', '').charAt(0)}</span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <strong style={{ fontSize: 15, fontWeight: 700, color: COLOR.ink }}>{r.name}</strong>
                    <Stars rating={r.rating} size={13} />
                  </span>
                  <span style={{ marginLeft: 'auto', opacity: 0.85 }}><GoogleG size={18} /></span>
                </div>
                <blockquote style={{ margin: 0, fontSize: 14.5, lineHeight: 1.65, color: COLOR.inkSoft }}>{r.text}</blockquote>
              </motion.figure>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── CLOSING CTA + FOOTER ───────────────────────────────────────────────────────
function MentorDuo() {
  return (
    <div className="m-duo">
      {MENTORS.map((m, i) => (
        <Fragment key={m.id}>
          {i > 0 && <span aria-hidden="true" className="m-duo-bar" />}
          <div className="m-duo-person">
            <img src={avatarUrl(m.photo, 192)} alt="" width={92} height={92}
              style={{ width: 92, height: 92, borderRadius: '50%', objectFit: 'cover', boxShadow: `0 0 0 3px ${COLOR.nightSoft}, 0 0 0 5px ${COLOR.gold}` }} />
            <span style={{ fontFamily: FONT_SERIF, fontStyle: 'italic', fontSize: 32, lineHeight: 1, color: '#fff' }}>{m.name}</span>
            <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: COLOR.gold }}>{m.role}</span>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

function ClosingSection() {
  return (
    <section className="m-pad-lg" style={{ position: 'relative', overflow: 'hidden', background: NIGHT, color: '#fff' }}>
      <Glow color="rgba(240,180,41,0.2)" style={{ width: 820, height: 820, left: '50%', top: '-46%', marginLeft: -410 }} />
      <Glow color="rgba(255,101,53,0.14)" style={{ width: 560, height: 560, left: '-20%', bottom: '-40%' }} />
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }} variants={stagger}
        style={{ ...container, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <motion.div variants={fadeUp}><MentorDuo /></motion.div>
        <motion.div variants={fadeUp} style={{ marginTop: 40 }}><Eyebrow onDark>{CLOSING.eyebrow}</Eyebrow></motion.div>
        <motion.h2 variants={fadeUp} style={{ margin: '16px 0 0', maxWidth: '18ch', fontSize: 'clamp(32px, 5vw, 58px)', fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1.05 }}>
          {CLOSING.heading} <Accent onDark>{CLOSING.headingAccent}</Accent>
        </motion.h2>
        <motion.p variants={fadeUp} style={{ margin: '18px 0 0', maxWidth: '50ch', fontSize: 'clamp(15.5px, 1.7vw, 17.5px)', lineHeight: 1.65, color: COLOR.onDarkSoft }}>{CLOSING.body}</motion.p>
        <motion.div variants={fadeUp} className="m-closing-ctas">
          <WhatsAppButton label={HERO.primaryCta} />
          <a href={`tel:${PHONE}`} className="m-btn m-btn--ghost"><Phone size={17} aria-hidden="true" /> Call {PHONE_LABEL}</a>
        </motion.div>
        <motion.ul variants={fadeUp} className="m-trust">
          {CLOSING.trust.map(t => (
            <li key={t}><Check size={16} aria-hidden="true" style={{ color: COLOR.gold, flexShrink: 0 }} /> {t}</li>
          ))}
        </motion.ul>
      </motion.div>
    </section>
  );
}

function MentorsFooter() {
  return (
    <footer className="m-footer" style={{ background: COLOR.mist, borderTop: `1px solid ${COLOR.rule}` }}>
      <div style={{ ...container, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
        <Link to="/" aria-label="Vista Tech home"><LogoMark height={36} /></Link>
        <p style={{ margin: '4px 0 0', fontSize: 13.5, fontWeight: 600, color: COLOR.ink }}>{FOOTER_INFO.line}</p>
        <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: COLOR.inkSoft }}>
          {FOOTER_INFO.address}
          <span aria-hidden="true" style={{ padding: '0 7px', color: COLOR.inkFaint }}>·</span>
          <a href={`tel:${PHONE}`} style={{ color: 'inherit', borderBottom: `1px solid ${COLOR.rule}` }}>{PHONE_LABEL}</a>
        </p>
        <nav aria-label="Footer" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '6px 18px', marginTop: 4 }}>
          {FOOTER_INFO.links.map(l => <Link key={l.to} to={l.to} className="m-footer-link">{l.label}</Link>)}
        </nav>
      </div>
    </footer>
  );
}

// phones only: WhatsApp + Call pinned to the bottom once the hero has scrolled away
function MobileCtaBar() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 560);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div className={`m-cta-bar${show ? ' is-on' : ''}`} aria-hidden={!show}>
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="m-btn m-btn--gold" tabIndex={show ? 0 : -1}><WhatsAppIcon size={18} /> WhatsApp us</a>
      <a href={`tel:${PHONE}`} className="m-btn m-btn--light" tabIndex={show ? 0 : -1}><Phone size={17} aria-hidden="true" /> Call</a>
    </div>
  );
}

// ─── GLOBAL STYLES ──────────────────────────────────────────────────────────────
// index.css pads every <section> by 60px on phones with !important, so the section
// padding here is scoped under .m-page (higher specificity) to win back control.
const GLOBAL_STYLE = `
.m-page .m-pad { padding-top: 68px !important; padding-bottom: 72px !important; }
@media (min-width: 900px) { .m-page .m-pad { padding-top: 104px !important; padding-bottom: 108px !important; } }
.m-page .m-pad-lg { padding-top: 76px !important; padding-bottom: 84px !important; }
@media (min-width: 900px) { .m-page .m-pad-lg { padding-top: 120px !important; padding-bottom: 124px !important; } }
.m-page .m-hero { padding-top: 84px !important; padding-bottom: 100px !important; }
@media (min-width: 960px) { .m-page .m-hero { padding-top: 104px !important; padding-bottom: 150px !important; } }
.m-page .m-stats-section { padding-top: 0 !important; padding-bottom: 0 !important; }
/* react-toastify's container is a <section> too, which leaves an empty 120px strip under the footer */
.m-page ~ .Toastify { padding: 0 !important; }

.m-page h1, .m-page h2 { text-wrap: balance; }
.m-sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }

.m-btn { display: inline-flex; align-items: center; justify-content: center; gap: 9px; min-height: 52px; padding: 0 24px; border: 0; border-radius: 999px; font-family: inherit; font-size: 15.5px; font-weight: 700; line-height: 1; text-decoration: none; white-space: nowrap; cursor: pointer; transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease; }
.m-btn:hover { transform: translateY(-2px); }
.m-btn:focus-visible { outline: 3px solid rgba(240,180,41,.65); outline-offset: 3px; }
.m-btn--gold { background: #F0B429; color: #1c1529; box-shadow: 0 14px 30px -12px rgba(240,180,41,.6); }
.m-btn--gold:hover { background: #f6c449; box-shadow: 0 18px 34px -12px rgba(240,180,41,.7); }
.m-btn--ghost { background: rgba(255,255,255,.06); color: #fff; box-shadow: inset 0 0 0 1.5px rgba(255,255,255,.3); }
.m-btn--ghost:hover { background: rgba(255,255,255,.12); }
.m-btn--light { background: #fff; color: #1c1529; }
.m-btn--sm { min-height: 42px; padding: 0 16px; font-size: 14px; gap: 7px; }

.m-header { position: absolute; top: 0; left: 0; right: 0; z-index: 10; padding-block: 14px; }
@media (min-width: 960px) { .m-header { padding-block: 22px; } }
.m-logo-pill { display: inline-flex; padding: 8px 14px; border-radius: 14px; background: #fff; box-shadow: 0 10px 30px -12px rgba(0,0,0,.55); }
.m-header-phone { display: none; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: rgba(255,255,255,.8); text-decoration: none; }
.m-header-phone:hover { color: #fff; }
@media (min-width: 700px) { .m-header-phone { display: inline-flex; } }

.m-dots { position: absolute; inset: 0; pointer-events: none; background-image: radial-gradient(rgba(255,255,255,.09) 1px, transparent 1.3px); background-size: 22px 22px; -webkit-mask-image: radial-gradient(ellipse 70% 60% at 72% 38%, #000, transparent 75%); mask-image: radial-gradient(ellipse 70% 60% at 72% 38%, #000, transparent 75%); }

.m-hero-grid { display: grid; grid-template-columns: minmax(0, 1fr); grid-template-areas: "photos" "copy"; gap: 30px; padding-top: 10px; }
.m-hero-photos { grid-area: photos; }
.m-hero-copy { grid-area: copy; display: flex; flex-direction: column; align-items: center; text-align: center; }
.m-hero-ctas { display: flex; flex-wrap: wrap; justify-content: center; gap: 12px; margin-top: 30px; }
.m-proof { display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 30px; }
@media (min-width: 960px) {
  .m-hero-grid { grid-template-columns: minmax(0, 1.05fr) minmax(0, .95fr); grid-template-areas: "copy photos"; align-items: center; gap: 36px; padding-top: 30px; }
  .m-hero-copy { align-items: flex-start; text-align: left; }
  .m-hero-ctas, .m-proof { justify-content: flex-start; }
}
@media (max-width: 480px) { .m-hero-ctas { flex-direction: column; width: 100%; } .m-hero-ctas .m-btn { width: 100%; } }

.m-portraits { position: relative; display: grid; grid-template-columns: 1fr 1fr; gap: 12px; max-width: 460px; margin: 0 auto; }
.m-portrait--second { margin-top: 36px; }
.m-chip { display: none; }
@media (min-width: 960px) {
  .m-portraits { display: block; height: 540px; max-width: 540px; margin: 0 0 0 auto; }
  .m-portrait { position: absolute; }
  .m-portrait--lead { left: 0; top: 88px; width: 58%; transform: rotate(-4deg); z-index: 2; }
  .m-portrait--second { right: 0; top: 8px; width: 50%; margin-top: 0; transform: rotate(5deg); z-index: 1; }
  .m-chip { display: block; position: absolute; z-index: 3; }
  .m-chip--a { left: -6%; top: 26px; }
  .m-chip--b { right: -3%; top: 382px; }
  /* the lead card covers the back card's lower left, so its name sits on the right */
  .m-portrait--second .m-portrait-cap { text-align: right; }
}
@keyframes m-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
@media (min-width: 960px) and (prefers-reduced-motion: no-preference) {
  .m-bob { animation: m-bob 7s ease-in-out infinite; }
  .m-bob--late { animation-delay: -3.5s; }
  .m-bob--slow { animation-duration: 9s; animation-delay: -2s; }
}

.m-stats { margin-top: -60px; display: grid; grid-template-columns: 1fr; background: #fff; border-radius: 24px; border: 1px solid rgba(43,34,64,.1); box-shadow: 0 34px 70px -38px rgba(29,20,48,.45); }
.m-stat { display: flex; align-items: center; gap: 18px; padding: 18px 22px; }
.m-stat + .m-stat { border-top: 1px solid rgba(43,34,64,.1); }
.m-stat-num { flex: 0 0 auto; min-width: 104px; margin: 0; font-size: 38px; font-weight: 800; letter-spacing: -0.045em; line-height: 1; color: #2b2240; font-variant-numeric: tabular-nums; }
.m-stat-label { margin: 0; font-size: 14.5px; font-weight: 600; line-height: 1.35; color: #5c5270; }
@media (min-width: 760px) {
  .m-stats { grid-template-columns: repeat(3, 1fr); margin-top: -84px; }
  .m-stat { flex-direction: column; align-items: flex-start; gap: 10px; padding: 32px 34px; }
  .m-stat + .m-stat { border-top: 0; border-left: 1px solid rgba(43,34,64,.1); }
  .m-stat-num { font-size: clamp(42px, 5vw, 58px); }
  .m-stat-label { font-size: 15px; }
}

.m-profile { display: grid; grid-template-columns: minmax(0, 1fr); gap: 36px; align-items: center; }
.m-profile-photo { position: relative; width: 100%; max-width: 400px; margin: 0 auto; }
.m-profile-frame { position: absolute; inset: 0; border-radius: 30px; border: 2px solid rgba(240,180,41,.8); transform: translate(14px, 14px); }
.m-profile--flip .m-profile-frame { transform: translate(-14px, 14px); }
.m-profile-badge { position: absolute; left: 16px; bottom: 16px; display: inline-flex; align-items: center; gap: 7px; padding: 8px 14px; border-radius: 999px; background: rgba(255,255,255,.96); color: #2b2240; font-size: 12.5px; font-weight: 700; box-shadow: 0 10px 24px -12px rgba(0,0,0,.45); }
.m-trained { display: inline-flex; align-items: center; gap: 8px; margin: 16px 0 0; padding: 8px 15px 8px 12px; border-radius: 999px; background: #fff5db; color: #8a5a00; font-size: 14px; font-weight: 600; }
.m-trained strong { font-weight: 800; color: #1c1529; }
.m-skills { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0 0; padding: 0; list-style: none; }
.m-skills li { padding: 8px 14px; border-radius: 999px; background: #f7f4fb; border: 1px solid rgba(43,34,64,.1); font-size: 13.5px; font-weight: 600; color: #2b2240; }
@media (min-width: 900px) {
  .m-profile { grid-template-columns: minmax(0, .85fr) minmax(0, 1.15fr); gap: 72px; }
  .m-profile--flip { grid-template-columns: minmax(0, 1.15fr) minmax(0, .85fr); }
  .m-profile--flip .m-profile-photo { order: 2; }
  .m-profile-photo { max-width: 440px; }
}

.m-steps { list-style: none; margin: 46px 0 0; padding: 0; display: grid; grid-template-columns: minmax(0, 1fr); gap: 14px; }
.m-step { position: relative; padding: 24px 22px 26px; border-radius: 20px; background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.1); }
.m-step::before { content: ""; position: absolute; left: 22px; right: 22px; top: -1px; height: 2px; border-radius: 2px; background: linear-gradient(90deg, #F0B429, #FF6535); }
@media (min-width: 680px) and (max-width: 1079px) { .m-steps { grid-template-columns: repeat(2, minmax(0, 1fr)); } .m-step:last-child { grid-column: 1 / -1; } }
@media (min-width: 1080px) { .m-steps { grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 16px; margin-top: 56px; } }

.m-video-row { display: grid; grid-auto-flow: column; grid-auto-columns: min(60vw, 260px); gap: 16px; overflow-x: auto; scroll-snap-type: x mandatory; scroll-padding-inline: 20px; margin: 18px -20px 0; padding: 4px 20px 14px; scrollbar-width: none; }
.m-video-row::-webkit-scrollbar { display: none; }
.m-video-card { scroll-snap-align: start; min-width: 0; }
@media (min-width: 900px) { .m-video-row { grid-auto-flow: row; grid-template-columns: repeat(var(--cols, 4), minmax(0, 1fr)); overflow: visible; margin: 22px 0 0; padding: 0; gap: 20px; } }
.m-video-frame { position: relative; aspect-ratio: 9 / 16; border-radius: 22px; overflow: hidden; background: #130d1d; box-shadow: 0 28px 50px -32px rgba(29,20,48,.6); }
.m-video-poster { all: unset; position: absolute; inset: 0; display: block; cursor: pointer; }
.m-video-poster:focus-visible { outline: 3px solid #F0B429; outline-offset: -3px; border-radius: 22px; }
.m-video-shade { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(19,13,29,0) 45%, rgba(19,13,29,.6) 100%); }
.m-video-play { position: absolute; left: 50%; top: 50%; width: 62px; height: 62px; margin: -31px 0 0 -31px; border-radius: 50%; display: grid; place-items: center; background: #F0B429; color: #1c1529; box-shadow: 0 0 0 8px rgba(240,180,41,.25), 0 16px 30px -10px rgba(0,0,0,.5); transition: transform .2s ease; }
.m-video-poster:hover .m-video-play { transform: scale(1.08); }
.m-video-pay { position: absolute; left: 12px; bottom: 12px; padding: 6px 11px; border-radius: 999px; background: rgba(255,255,255,.96); color: #1c1529; font-size: 13px; font-weight: 800; letter-spacing: -0.01em; }
.m-video-bg { display: inline-block; margin-top: 10px; padding: 5px 10px; border-radius: 999px; background: #fff5db; color: #8a5a00; font-size: 12px; font-weight: 700; }

.m-reviews-head { display: flex; flex-direction: column; align-items: center; gap: 24px; }
.m-rating { display: inline-flex; align-items: center; gap: 14px; padding: 14px 20px; border-radius: 18px; background: #fff; border: 1px solid rgba(43,34,64,.1); box-shadow: 0 18px 40px -30px rgba(29,20,48,.45); text-decoration: none; transition: transform .18s ease; }
.m-rating:hover { transform: translateY(-2px); }
@media (min-width: 900px) {
  .m-reviews-head { flex-direction: row; align-items: flex-end; justify-content: space-between; }
  .m-head-left { align-items: flex-start !important; text-align: left !important; }
}
.m-reviews { display: grid; gap: 16px; margin-top: 36px; }
.m-review-featured { margin: 0; padding: 30px 26px; border-radius: 26px; background: linear-gradient(160deg, #2b2240, #4e3a69); color: #fff; box-shadow: 0 36px 70px -40px rgba(29,20,48,.7); display: flex; flex-direction: column; }
.m-review-grid { display: grid; grid-auto-flow: column; grid-auto-columns: min(80vw, 330px); gap: 14px; overflow-x: auto; scroll-snap-type: x mandatory; scroll-padding-inline: 20px; margin: 0 -20px; padding: 2px 20px 14px; scrollbar-width: none; }
.m-review-grid::-webkit-scrollbar { display: none; }
.m-review { margin: 0; scroll-snap-align: start; display: flex; flex-direction: column; gap: 14px; padding: 22px 20px; border-radius: 20px; background: #fff; border: 1px solid rgba(43,34,64,.1); box-shadow: 0 18px 40px -32px rgba(29,20,48,.4); }
@media (min-width: 900px) {
  .m-reviews { grid-template-columns: minmax(0, .95fr) minmax(0, 1.05fr); gap: 18px; margin-top: 44px; }
  .m-review-featured { padding: 40px 38px; }
  .m-review-grid { grid-auto-flow: row; grid-template-columns: repeat(2, minmax(0, 1fr)); overflow: visible; margin: 0; padding: 0; gap: 18px; }
}

.m-duo { display: flex; align-items: center; justify-content: center; gap: 22px; }
.m-duo-person { display: flex; flex-direction: column; align-items: center; gap: 10px; }
.m-duo-bar { width: 2px; height: 110px; border-radius: 2px; background: linear-gradient(180deg, transparent, #F0B429, transparent); }
@media (min-width: 600px) { .m-duo { gap: 44px; } }
.m-closing-ctas { display: flex; flex-wrap: wrap; justify-content: center; gap: 12px; margin-top: 32px; }
@media (max-width: 480px) { .m-closing-ctas { flex-direction: column; width: 100%; } .m-closing-ctas .m-btn { width: 100%; } }
.m-trust { list-style: none; width: 100%; max-width: 760px; margin: 34px 0 0; padding: 24px 0 0; border-top: 1px solid rgba(255,255,255,.12); display: flex; flex-wrap: wrap; justify-content: center; gap: 10px 26px; }
.m-trust li { display: inline-flex; align-items: center; gap: 8px; font-size: 13.5px; color: rgba(255,255,255,.74); }

.m-footer { padding: 30px 0 110px; }
@media (min-width: 760px) { .m-footer { padding-bottom: 34px; } }
.m-footer-link { font-size: 12.5px; color: #5c5270; text-decoration: none; border-bottom: 1px solid rgba(43,34,64,.2); }
.m-footer-link:hover { color: #2b2240; }

.m-cta-bar { position: fixed; left: 12px; right: 12px; bottom: calc(12px + env(safe-area-inset-bottom)); z-index: 50; display: grid; grid-template-columns: 1.5fr 1fr; gap: 10px; padding: 10px; border-radius: 20px; background: rgba(19,13,29,.92); -webkit-backdrop-filter: blur(12px); backdrop-filter: blur(12px); box-shadow: 0 18px 40px -16px rgba(0,0,0,.6); transform: translateY(150%); visibility: hidden; transition: transform .3s ease, visibility 0s linear .3s; }
.m-cta-bar.is-on { transform: none; visibility: visible; transition: transform .3s ease; }
.m-cta-bar .m-btn { min-height: 48px; padding: 0 14px; font-size: 14.5px; }
@media (min-width: 760px) { .m-cta-bar { display: none; } }
`;

// ─── PAGE ───────────────────────────────────────────────────────────────────────
export default function Mentors() {
  useSEO();
  const [playing, setPlaying] = useState<string | null>(null);

  return (
    <MotionConfig reducedMotion="user">
      <div className="m-page" style={{ position: 'relative', background: COLOR.paper, color: COLOR.ink, fontFamily: FONT_BODY, minHeight: '100vh', overflowX: 'clip' }}>
        <style>{GLOBAL_STYLE}</style>
        <MentorsHeader />
        <main>
          <Hero />
          <StatsBand />
          <MentorsSection />
          <MethodSection />
          <StoriesSection playing={playing} setPlaying={setPlaying} />
          <FeedbackSection playing={playing} setPlaying={setPlaying} />
          <ReviewsSection />
          <ClosingSection />
        </main>
        <MentorsFooter />
        <MobileCtaBar />
      </div>
    </MotionConfig>
  );
}
